// src/components/EditProject.js
import React, { useState, useEffect } from "react";
import {
  Autocomplete,
  FormControl,
  Checkbox,
  FormControlLabel,
  Select,
  MenuItem,
  TextField,
} from "@mui/material";
import { db, storage } from "../firebaseConfig";
import { doc, updateDoc, getDoc } from "firebase/firestore";
import {
  collection,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import CollapsibleSection from "./forms/CollapsibleSection";
import FormField from "./forms/FormField";
import TaskCard from "./forms/TaskCard";
import {
  ClipboardIcon,
  UserGroupIcon,
  CreditCardIcon,
  TasksIcon,
  DocumentIcon,
  UserIcon,
  BriefcaseIcon,
  CurrencyIcon,
  CalendarIcon,
  PlusIcon,
  CheckCircleIcon,
  HashIcon,
  TagIcon,
} from "./icons/FormIcons";
import { useTheme } from "../ThemeContext";
import { buildTasksSearchText } from "../utils/tasksSearchText";
import { buildNumericAmountFields } from "../utils/numericFields";
import { deriveInvoiceRaised } from "../utils/status";

const AMOUNT_FIELD_NAMES = new Set(["amountIncVat", "cost", "income", "vat"]);
const MAX_FILE_SIZE_BYTES = 15 * 1024 * 1024; // 15 MB per file

// Define the task, country, and analyst options directly
const taskOptions = [
  "DSS",
  "BC",
  "SR",
  "PS",
  "VS",
  "IMEQ",
  "BEQ",
  "FL",
  "SS",
  "EQ",
  "LC",
  "MA",
  "RPS",
  "SV",
  "VES",
];

const countryOptions = [
  "Afghanistan",
  "Algeria",
  "Bahrain",
  "Djibouti",
  "Egypt",
  "Iran",
  "Iraq",
  "Jordan",
  "Kuwait",
  "Lebanon",
  "Libya",
  "Mauritania",
  "Morocco",
  "Oman",
  "Pakistan",
  "Palestine",
  "Qatar",
  "Saudi Arabia",
  "Somalia",
  "Sudan",
  "Syria",
  "Tunisia",
  "Turkey",
  "UAE",
  "UAE - Abu Dhabi",
  "UAE - Ajman",
  "UAE - Dubai",
  "UAE - Fujairah",
  "UAE - RAK",
  "UAE - Sharjah",
  "UAE - UAQ",
  "Yemen",
];

const projectManagerOptions = [
  "Zak",
  "Omar",
  "Aseel",
  "Muhab",
  "Mustafa",
  "Sadegh",
];
const companyOptions = ["UK", "UAE", "Unassigned"];

const getCompanyFlagCode = (company) => {
  if (company === "UK") return "gb";
  if (company === "UAE") return "ae";
  return "xx";
};

const CompanyFlagIcon = ({ company, size = 18 }) => {
  if (company === "Unassigned") {
    return (
      <div
        className="flex items-center justify-center rounded-full bg-slate-200 text-slate-600 font-bold"
        style={{
          width: size,
          height: size,
          fontSize: size * 0.55,
          boxShadow: "0 2px 6px rgba(0, 0, 0, 0.08)",
        }}
      >
        ?
      </div>
    );
  }

  return (
    <img
      src={`https://hatscripts.github.io/circle-flags/flags/${getCompanyFlagCode(
        company
      )}.svg`}
      alt={company ? `${company} flag` : "Flag"}
      width={size}
      height={size}
      className="flex-shrink-0"
      style={{
        borderRadius: "50%",
        boxShadow: "0 2px 6px rgba(0, 0, 0, 0.12)",
        border: "1px solid rgba(15, 23, 42, 0.08)",
        backgroundColor: "#fff",
      }}
      loading="lazy"
    />
  );
};

const EditProject = ({ projectId, onClose }) => {
  const { theme } = useTheme();
  const dropdownMenuProps = {
    PaperProps: {
      sx: {
        borderRadius: "1rem",
        marginTop: "0.5rem",
        boxShadow:
          "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
        "& .MuiList-root": { padding: "0.5rem" },
        "& .MuiMenuItem-root": {
          borderRadius: "0.5rem",
          marginBottom: "0.25rem",
          "&:last-child": { marginBottom: 0 },
          "&.Mui-selected": {
            backgroundColor: "#e2e8f0",
            color: "#0f172a",
            fontWeight: 600,
            "&:hover": { backgroundColor: "#cbd5e1" },
          },
          "&:hover": { backgroundColor: "#f3f4f6" },
        },
      },
    },
  };

  const autocompleteProps = {
    paper: {
      sx: {
        borderRadius: "1rem",
        marginTop: "0.5rem",
        boxShadow:
          "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
        "& .MuiAutocomplete-listbox": { padding: "0.5rem" },
        "& .MuiAutocomplete-option": {
          borderRadius: "0.5rem",
          marginBottom: "0.25rem",
          "&:last-child": { marginBottom: 0 },
          '&[aria-selected="true"]': {
            backgroundColor: "#e2e8f0 !important",
            color: "#0f172a",
            fontWeight: 600,
          },
          "&.Mui-focused": { backgroundColor: "#f3f4f6 !important" },
        },
      },
    },
  };

  const [project, setProject] = useState({
    projectRef: "",
    projectBriefing: "",
    invoiceNo: "",
    projectStatus: "",
    company: "Unassigned",
    projectManager: "",
    cost: "",
    income: "",
    vat: "0",
    amountIncVat: "",
    vatAED: "5",
    client: "",
    poc: "",
    dateForInvoice: "",
    invoiceRaised: "No",
    paymentStatus: "",
    paidOn: "",
    paymentNotes: "",
    documents: [],
    tasks: [
      {
        taskNo: "",
        subject: "",
        country: "",
        task: "",
        orderAccepted: "",
        dueDate: "",
        taskStatus: "",
        analyst1: "",
        analyst2: "",
        income: "",
        vat: "0",
        amountIncVat: "",
        incomeAED: "",
        vatAED: "5",
        amountIncVatAED: "",
        completedOn: "",
      },
    ],
  });

  const [applyVatToAllTasks, setApplyVatToAllTasks] = useState(true);
  const [submitDisabled, setSubmitDisabled] = useState(false);
  const [clientOptions, setClientOptions] = useState([]);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [documentFiles, setDocumentFiles] = useState([]);
  const [fileError, setFileError] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState("");
  const [newlyAddedTaskIndex, setNewlyAddedTaskIndex] = useState(null);

  useEffect(() => {
    const fetchProject = async () => {
      const docRef = doc(db, "projects", projectId);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const rawProjectData = {
          company: "Unassigned",
          documents: [],
          ...docSnap.data(),
        };
        const tasks = Array.isArray(rawProjectData.tasks)
          ? rawProjectData.tasks.filter(Boolean)
          : [];
        const firstTaskVat = tasks.find((task) => task?.vat !== undefined)?.vat;
        const firstTaskVatAED = tasks.find((task) => task?.vatAED !== undefined)
          ?.vatAED;
        const resolvedVat = rawProjectData.vat ?? firstTaskVat ?? "0";
        const resolvedVatAED = rawProjectData.vatAED ?? firstTaskVatAED ?? "5";

        const normalizeVatValue = (value) => {
          if (value === null || value === undefined) return "";
          const asString = String(value).trim();
          return asString;
        };
        const vatValues = tasks
          .map((task) => normalizeVatValue(task?.vat))
          .filter(Boolean);
        const vatAEDValues = tasks
          .map((task) => normalizeVatValue(task?.vatAED))
          .filter(Boolean);

        const isUniform = (values) =>
          values.length === 0 ? true : values.every((val) => val === values[0]);

        const isVatUniform = isUniform(vatValues);
        const isVatAEDUniform = isUniform(vatAEDValues);
        const matchesProjectVat =
          !normalizeVatValue(rawProjectData.vat) ||
          normalizeVatValue(rawProjectData.vat) === normalizeVatValue(resolvedVat);
        const matchesProjectVatAED =
          !normalizeVatValue(rawProjectData.vatAED) ||
          normalizeVatValue(rawProjectData.vatAED) ===
            normalizeVatValue(resolvedVatAED);
        const initialApplyVatToAllTasks =
          isVatUniform && isVatAEDUniform && matchesProjectVat && matchesProjectVatAED;

        const projectData = {
          ...rawProjectData,
          vat: resolvedVat,
          vatAED: resolvedVatAED,
          tasks:
            tasks.length > 0
              ? tasks
              : [
                  {
                    taskNo: 1,
                    subject: "",
                    country: "",
                    task: "",
                    orderAccepted: "",
                    dueDate: "",
                    taskStatus: "",
                    analyst1: "",
                    analyst2: "",
                    income: "",
                    vat: resolvedVat,
                    amountIncVat: "",
                    incomeAED: "",
                    vatAED: resolvedVatAED,
                    amountIncVatAED: "",
                    completedOn: "",
                  },
                ],
        };
        const invoiceRaised = deriveInvoiceRaised(
          projectData.dateForInvoice,
          projectData.invoiceRaised
        );
        setProject({ ...projectData, invoiceRaised });
        setApplyVatToAllTasks(initialApplyVatToAllTasks);

        // Fetch POCs for the current client
        if (projectData.client) {
          const pocs = await fetchPointsOfContact(projectData.client);
          setProject((prev) => ({
            ...prev,
            pocs: pocs,
          }));
        }
      }
    };

    const fetchClients = async () => {
      const clientsCollection = collection(db, "clients");
      const snapshot = await getDocs(clientsCollection);
      const clients = snapshot.docs.map((doc) => doc.data().clientName);
      setClientOptions(clients);
    };

    fetchProject();
    fetchClients();
  }, [projectId]);

  const fetchPointsOfContact = async (clientName) => {
    const clientsCollection = collection(db, "clients");
    const clientQuery = query(
      clientsCollection,
      where("clientName", "==", clientName)
    );
    const clientSnapshot = await getDocs(clientQuery);
    if (!clientSnapshot.empty) {
      const clientData = clientSnapshot.docs[0].data();
      return clientData.pointOfContacts || [];
    }
    return [];
  };

  const validateField = (name, value) => {
    // All fields are optional now
    return null;
  };

  const handleBlur = (fieldName) => {
    setTouched({ ...touched, [fieldName]: true });
    const error = validateField(fieldName, project[fieldName]);
    setErrors({ ...errors, [fieldName]: error });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const updatedProject = { ...project, [name]: value };
    const withInvoiceRaised =
      name === "dateForInvoice"
        ? {
            ...updatedProject,
            invoiceRaised: deriveInvoiceRaised(value, project.invoiceRaised),
          }
        : updatedProject;
    const numericFields = AMOUNT_FIELD_NAMES.has(name)
      ? buildNumericAmountFields(withInvoiceRaised)
      : {};
    setProject({ ...withInvoiceRaised, ...numericFields });

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({ ...errors, [name]: null });
    }
  };

  const formatFileSize = (size) => {
    if (!size) return "0 B";
    const units = ["B", "KB", "MB", "GB"];
    const index = Math.floor(Math.log(size) / Math.log(1024));
    const value = size / Math.pow(1024, index);
    return `${value.toFixed(1)} ${units[index]}`;
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    let rejectedMessage = "";
    const validFiles = files.filter((file) => {
      if (file.size > MAX_FILE_SIZE_BYTES) {
        rejectedMessage = `"${file.name}" exceeds the 15 MB limit.`;
        return false;
      }
      return true;
    });

    setDocumentFiles((prev) => [...prev, ...validFiles]);
    setFileError(rejectedMessage);
    setUploadError("");

    // Allow selecting the same file again after removal
    e.target.value = null;
  };

  const handleRemoveFile = (index) => {
    setDocumentFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const toFiniteNumber = (value) => {
    if (value === null || value === undefined || value === "") return null;
    const parsed =
      typeof value === "number"
        ? value
        : parseFloat(String(value).replace(/,/g, ""));
    return Number.isFinite(parsed) ? parsed : null;
  };

  const calculateAmountIncVat = (income, vatRate) => {
    const incomeNumber = toFiniteNumber(income);
    if (incomeNumber === null) return "";
    const vatNumber = toFiniteNumber(vatRate) ?? 0;
    const amount = incomeNumber + (incomeNumber * vatNumber) / 100;
    return Number.isFinite(amount) ? amount.toFixed(2) : "";
  };

  const sumByNumber = (items, selector) =>
    items.reduce((acc, item) => {
      const num = toFiniteNumber(selector(item));
      return acc + (num ?? 0);
    }, 0);

  const applyProjectVatToTasks = (currentProject, overrides = {}) => {
    const vatRate = overrides.vat ?? currentProject.vat;
    const vatAEDRate = overrides.vatAED ?? currentProject.vatAED;
    const updatedTasks = (currentProject.tasks || []).map((task) => ({
      ...task,
      vat: vatRate,
      amountIncVat: calculateAmountIncVat(task.income, vatRate),
      vatAED: vatAEDRate,
      amountIncVatAED: calculateAmountIncVat(task.incomeAED, vatAEDRate),
    }));

    const updatedIncome = sumByNumber(updatedTasks, (task) => task.income);
    const updatedAmountIncVat = sumByNumber(
      updatedTasks,
      (task) => task.amountIncVat
    );

    return {
      ...currentProject,
      tasks: updatedTasks,
      income: updatedIncome.toFixed(2),
      amountIncVat: updatedAmountIncVat.toFixed(2),
    };
  };

  const handleApplyVatToAllTasksChange = (event) => {
    const checked = event.target.checked;
    setApplyVatToAllTasks(checked);
    if (!checked) return;
    setProject((prevState) => {
      const updatedProject = applyProjectVatToTasks(prevState);
      const numericFields = buildNumericAmountFields(updatedProject);
      return { ...updatedProject, ...numericFields };
    });
  };

  const handleProjectVatChange = (event) => {
    const nextVat = event.target.value;
    setProject((prevState) => {
      const baseProject = { ...prevState, vat: nextVat };
      const updatedProject = applyVatToAllTasks
        ? applyProjectVatToTasks(baseProject, { vat: nextVat })
        : baseProject;
      const numericFields = buildNumericAmountFields(updatedProject);
      return { ...updatedProject, ...numericFields };
    });
  };

  const handleProjectVatAEDChange = (event) => {
    const nextVatAED = event.target.value;
    setProject((prevState) => {
      const baseProject = { ...prevState, vatAED: nextVatAED };
      const updatedProject = applyVatToAllTasks
        ? applyProjectVatToTasks(baseProject, { vatAED: nextVatAED })
        : baseProject;
      const numericFields = buildNumericAmountFields(updatedProject);
      return { ...updatedProject, ...numericFields };
    });
  };

  const uploadProjectDocuments = async () => {
    if (!documentFiles.length) return [];
    setUploadProgress(0);

    const uploadedDocs = [];

    for (let i = 0; i < documentFiles.length; i++) {
      const file = documentFiles[i];
      const safeName = file.name.replace(/\s+/g, "_");
      const storagePath = `projects/${projectId}/${Date.now()}-${i}-${safeName}`;
      const storageRef = ref(storage, storagePath);

      const docMeta = await new Promise((resolve, reject) => {
        const uploadTask = uploadBytesResumable(storageRef, file);

        uploadTask.on(
          "state_changed",
          (snapshot) => {
            const base = (i / documentFiles.length) * 100;
            const incremental =
              (snapshot.bytesTransferred / snapshot.totalBytes) *
              (100 / documentFiles.length);
            const percent = Math.min(99, Math.round(base + incremental));
            setUploadProgress(percent);
          },
          (error) => {
            reject(error);
          },
          async () => {
            try {
              const url = await getDownloadURL(uploadTask.snapshot.ref);
              const percentComplete = Math.round(
                ((i + 1) / documentFiles.length) * 100
              );
              setUploadProgress(percentComplete);
              resolve({
                name: file.name,
                url,
                path: storagePath,
                contentType: file.type || "application/octet-stream",
                size: file.size,
                uploadedAt: new Date(),
              });
            } catch (err) {
              reject(err);
            }
          }
        );
      });

      uploadedDocs.push(docMeta);
    }

    setUploadProgress(100);
    return uploadedDocs;
  };

  const handleTaskChange = (index, e) => {
    const { name, value } = e.target;
    const newTasks = [...project.tasks];
    newTasks[index][name] = value;

    if (name === "income" || name === "vat") {
      const vatValue = name === "vat" ? value : newTasks[index].vat;
      const incomeValue = name === "income" ? value : newTasks[index].income;
      newTasks[index].amountIncVat = calculateAmountIncVat(
        incomeValue,
        vatValue
      );
    }

    if (name === "incomeAED" || name === "vatAED") {
      const vatAEDValue = name === "vatAED" ? value : newTasks[index].vatAED;
      const incomeAEDValue =
        name === "incomeAED" ? value : newTasks[index].incomeAED;
      newTasks[index].amountIncVatAED = calculateAmountIncVat(
        incomeAEDValue,
        vatAEDValue
      );
    }

    const updatedIncome = sumByNumber(newTasks, (task) => task.income);
    const updatedAmountIncVat = sumByNumber(
      newTasks,
      (task) => task.amountIncVat
    );

    const updatedProject = {
      ...project,
      tasks: newTasks,
      income: updatedIncome.toFixed(2),
      amountIncVat: updatedAmountIncVat.toFixed(2),
    };
    const numericFields = buildNumericAmountFields(updatedProject);

    setProject((prevState) => ({
      ...prevState,
      ...updatedProject,
      ...numericFields,
    }));
  };

  const handleAddTask = () => {
    const newTaskIndex = project.tasks.length;
    const newTaskNo = newTaskIndex + 1;
    setProject({
      ...project,
      tasks: [
        ...project.tasks,
        {
          taskNo: newTaskNo,
          subject: "",
          country: "",
          task: "",
          orderAccepted: "",
          dueDate: "",
          taskStatus: "",
          analyst1: "",
          analyst2: "",
          income: "",
          vat: project.vat,
          amountIncVat: "",
          incomeAED: "",
          vatAED: project.vatAED,
          amountIncVatAED: "",
          completedOn: "",
        },
      ],
    });
    setNewlyAddedTaskIndex(newTaskIndex);
  };

  const handleRemoveTask = (index) => {
    const newTasks = project.tasks.filter((_, i) => i !== index);

    const updatedIncome = sumByNumber(newTasks, (task) => task.income);
    const updatedAmountIncVat = sumByNumber(
      newTasks,
      (task) => task.amountIncVat
    );

    const updatedProject = {
      ...project,
      tasks: newTasks,
      income: updatedIncome.toFixed(2),
      amountIncVat: updatedAmountIncVat.toFixed(2),
    };
    const numericFields = buildNumericAmountFields(updatedProject);

    setProject((prevState) => ({
      ...prevState,
      ...updatedProject,
      ...numericFields,
    }));
  };

  const isFormValid = () => {
    // All fields are optional now
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitDisabled) return;

    try {
      setSubmitDisabled(true);
      setUploadProgress(0);
      setUploadError("");
      setFileError("");
      const projectRef = doc(db, "projects", projectId);
      const tasksSearchText = buildTasksSearchText(project.tasks || []);
      const numericFields = buildNumericAmountFields(project);
      const invoiceRaised = deriveInvoiceRaised(
        project.dateForInvoice,
        project.invoiceRaised
      );
      const uploadedDocuments = await uploadProjectDocuments();
      // Avoid overwriting invoices that may have been updated elsewhere (e.g. after generating a new invoice)
      const { invoices: _ignoredInvoices, documents = [], ...projectWithoutFiles } =
        project || {};
      const updatedDocuments = [...documents, ...uploadedDocuments];
      await updateDoc(projectRef, {
        ...projectWithoutFiles,
        ...numericFields,
        invoiceRaised,
        dateForInvoice: project.dateForInvoice || "N/A",
        paidOn: project.paidOn || "N/A",
        tasksSearchText,
        documents: updatedDocuments,
      });
      setDocumentFiles([]);
      onClose();
    } catch (error) {
      console.error("Error updating document: ", error);
      setUploadError("Upload failed. Please try again.");
    } finally {
      setSubmitDisabled(false);
    }
  };

  const taskSummary = `${project.tasks.length} ${
    project.tasks.length === 1 ? "task" : "tasks"
  }${
    project.tasks.some((t) => t.amountIncVat)
      ? ` • £${project.tasks
          .reduce((sum, t) => sum + (parseFloat(t.amountIncVat) || 0), 0)
          .toFixed(2)}`
      : ""
  }`;
  const showAedFields = project.company !== "UK";

  return (
    <div className="flex flex-col h-full overflow-hidden rounded-2xl">
      {/* Fixed Header */}
      <div className={`${theme.formHeaderBg} px-8 py-5 rounded-t-2xl`}>
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-white/10 flex items-center justify-center backdrop-blur-sm">
              <ClipboardIcon className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Edit Project</h1>
              <p className={`text-sm ${theme.formSubText} mt-0.5`}>
                Update project details, tasks, client information, and payment
                status
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white/80 hover:text-white hover:bg-white/10 rounded-lg p-2 transition-all duration-200"
            type="button"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="overflow-y-auto flex-1 bg-slate-50">
        <div className="p-6">
          {/* Main Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Project Information Section */}
            <CollapsibleSection
              title="Project Information"
              icon={<ClipboardIcon />}
              defaultOpen={true}
            >
              <div className="space-y-2">
                {/* Row 1: Company, Project Ref, Invoice No */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <FormField
                    label="Company"
                    icon={<BriefcaseIcon className="w-4 h-4" />}
                  >
                    <FormControl fullWidth>
                      <Select
                        MenuProps={dropdownMenuProps}
                        name="company"
                        value={project.company || "Unassigned"}
                        onChange={handleInputChange}
                        className="rounded-xl"
                        renderValue={(value) => (
                          <span className="flex items-center gap-2">
                            <CompanyFlagIcon company={value} size={18} />
                            <span>{value}</span>
                          </span>
                        )}
                        sx={{
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#d1d5db",
                            borderRadius: "0.75rem",
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#9ca3af",
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#0f172a",
                            borderWidth: "2px",
                          },
                          "& .MuiSelect-select": {
                            padding: "12px 16px",
                            fontSize: "0.875rem",
                            minHeight: "unset",
                            lineHeight: "1.55",
                          },
                        }}
                      >
                        {companyOptions.map((option) => (
                          <MenuItem key={option} value={option}>
                            <span className="flex items-center gap-2">
                              <CompanyFlagIcon company={option} size={18} />
                              <span>{option}</span>
                            </span>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </FormField>

                  <FormField
                    label="Project Reference"
                    icon={<TagIcon className="w-4 h-4" />}
                  >
                    <input
                      type="text"
                      name="projectRef"
                      value={project.projectRef}
                      onChange={handleInputChange}
                      placeholder="Enter project reference"
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm text-gray-900
                             focus:ring-2 focus:ring-slate-500 focus:border-slate-500
                             hover:border-gray-400 transition-all duration-200"
                    />
                  </FormField>

                  <FormField
                    label="Invoice Number"
                    icon={<HashIcon className="w-4 h-4" />}
                  >
                    <input
                      type="text"
                      name="invoiceNo"
                      value={project.invoiceNo}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm text-gray-900
                             focus:ring-2 focus:ring-slate-500 focus:border-slate-500
                             hover:border-gray-400 transition-all duration-200"
                    />
                  </FormField>
                </div>

                {/* Row 2: Project Briefing */}
                <FormField
                  label="Project Briefing"
                  icon={<DocumentIcon className="w-4 h-4" />}
                  error={touched.projectBriefing && errors.projectBriefing}
                  value={project.projectBriefing}
                >
                  <textarea
                    name="projectBriefing"
                    value={project.projectBriefing}
                    onChange={handleInputChange}
                    onBlur={() => handleBlur("projectBriefing")}
                    rows={3}
                    placeholder="Describe the project scope, objectives, and key deliverables..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm text-gray-900
                           focus:ring-2 focus:ring-slate-500 focus:border-slate-500
                           hover:border-gray-400 transition-all duration-200 resize-none"
                  />
                </FormField>

                {/* Row 3: Project Manager, Cost */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    label="Project Manager"
                    icon={<UserIcon className="w-4 h-4" />}
                    error={touched.projectManager && errors.projectManager}
                    value={project.projectManager}
                  >
                    <Autocomplete
                      componentsProps={autocompleteProps}
                      options={projectManagerOptions}
                      value={project.projectManager}
                      onChange={(event, newValue) => {
                        setProject({ ...project, projectManager: newValue });
                        if (errors.projectManager) {
                          setErrors({ ...errors, projectManager: null });
                        }
                      }}
                      onBlur={() => handleBlur("projectManager")}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select project manager..."
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "0.75rem",
                              padding: "0",
                              "& fieldset": { borderColor: "#d1d5db" },
                              "&:hover fieldset": { borderColor: "#9ca3af" },
                              "&.Mui-focused fieldset": {
                                borderColor: "#0f172a",
                                borderWidth: "2px",
                              },
                              "& .MuiOutlinedInput-input": {
                                padding: "12px 16px",
                                fontSize: "0.875rem",
                              },
                            },
                          }}
                        />
                      )}
                    />
                  </FormField>

                  <FormField
                    label="Cost"
                    icon={<CurrencyIcon className="w-4 h-4" />}
                  >
                    <input
                      type="text"
                      name="cost"
                      value={project.cost}
                      onChange={handleInputChange}
                      placeholder="0.00"
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm text-gray-900
                             focus:ring-2 focus:ring-slate-500 focus:border-slate-500
                             hover:border-gray-400 transition-all duration-200"
                    />
                  </FormField>
                </div>

                {/* Row 4: Project Status */}
                <FormField
                  label="Project Status"
                  icon={<CheckCircleIcon className="w-4 h-4" />}
                >
                  <input
                    type="text"
                    name="projectStatus"
                    value={project.projectStatus}
                    onChange={handleInputChange}
                    placeholder="e.g., In Progress, Completed, On Hold..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm text-gray-900
                           focus:ring-2 focus:ring-slate-500 focus:border-slate-500
                           hover:border-gray-400 transition-all duration-200"
                  />
                </FormField>
              </div>
            </CollapsibleSection>

            {/* Client & Contact Details Section */}
            <CollapsibleSection
              title="Client & Contact Details"
              icon={<UserGroupIcon />}
              defaultOpen={true}
            >
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    label="Client"
                    icon={<BriefcaseIcon className="w-4 h-4" />}
                    error={touched.client && errors.client}
                    value={project.client}
                  >
                    <Autocomplete
                      componentsProps={autocompleteProps}
                      freeSolo
                      options={clientOptions}
                      value={project.client}
                      onInputChange={async (event, newValue) => {
                        setProject((prev) => ({ ...prev, client: newValue }));
                        if (errors.client) {
                          setErrors({ ...errors, client: null });
                        }
                        if (!newValue) {
                          setProject((prev) => ({ ...prev, pocs: [], poc: "" }));
                          return;
                        }

                        const pocs = await fetchPointsOfContact(newValue);
                        setProject((prevState) => {
                          const availablePocs = Array.isArray(pocs) ? pocs : [];
                          const keepExistingPoc =
                            prevState.client === newValue &&
                            prevState.poc &&
                            availablePocs.includes(prevState.poc);
                          return {
                            ...prevState,
                            client: newValue,
                            pocs: availablePocs,
                            poc: keepExistingPoc
                              ? prevState.poc
                              : availablePocs[0] || "",
                          };
                        });
                      }}
                      onBlur={() => handleBlur("client")}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select or enter client name..."
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "0.75rem",
                              padding: "0",
                              "& fieldset": { borderColor: "#d1d5db" },
                              "&:hover fieldset": { borderColor: "#9ca3af" },
                              "&.Mui-focused fieldset": {
                                borderColor: "#0f172a",
                                borderWidth: "2px",
                              },
                              "& .MuiOutlinedInput-input": {
                                padding: "12px 16px",
                                fontSize: "0.875rem",
                              },
                            },
                          }}
                        />
                      )}
                    />
                  </FormField>

                  <FormField
                    label="Point of Contact"
                    icon={<UserIcon className="w-4 h-4" />}
                  >
                    <Autocomplete
                      componentsProps={autocompleteProps}
                      freeSolo
                      options={project.pocs || []}
                      value={project.poc}
                      onInputChange={(event, newValue) => {
                        setProject({ ...project, poc: newValue });
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select or enter contact..."
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "0.75rem",
                              padding: "0",
                              "& fieldset": { borderColor: "#d1d5db" },
                              "&:hover fieldset": { borderColor: "#9ca3af" },
                              "&.Mui-focused fieldset": {
                                borderColor: "#0f172a",
                                borderWidth: "2px",
                              },
                              "& .MuiOutlinedInput-input": {
                                padding: "12px 16px",
                                fontSize: "0.875rem",
                              },
                            },
                          }}
                        />
                      )}
                    />
                  </FormField>
                </div>
              </div>
            </CollapsibleSection>

            {/* Payment & Invoice Information Section */}
            <CollapsibleSection
              title="Payment & Invoice Information"
              icon={<CreditCardIcon />}
            >
              <div className="space-y-4">
                {/* Row 1: Date for Invoice, Invoice Raised */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    label="Date for Invoice"
                    icon={<CalendarIcon className="w-4 h-4" />}
                  >
                    <input
                      type="date"
                      name="dateForInvoice"
                      value={project.dateForInvoice}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm text-gray-900
                             focus:ring-2 focus:ring-slate-500 focus:border-slate-500
                             hover:border-gray-400 transition-all duration-200"
                    />
                  </FormField>

                  <FormField
                    label="Invoice Raised"
                    icon={<ClipboardIcon className="w-4 h-4" />}
                  >
                    <div className="flex items-center justify-between px-4 py-3 border border-gray-200 rounded-xl bg-gray-50">
                      <span
                        className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                          project.invoiceRaised === "Yes"
                            ? "bg-green-100 text-green-800"
                            : "bg-gray-200 text-gray-700"
                        }`}
                      >
                        {project.invoiceRaised === "Yes" ? "Yes" : "No"}
                      </span>
                      <span className="text-xs text-gray-500">
                        Auto-updates when invoice date is set.
                      </span>
                    </div>
                  </FormField>
                </div>

                {/* Row 2: Payment Status, Paid On */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    label="Payment Status"
                    icon={<CheckCircleIcon className="w-4 h-4" />}
                  >
                    <input
                      type="text"
                      name="paymentStatus"
                      value={project.paymentStatus}
                      onChange={handleInputChange}
                      placeholder="e.g., Pending, Paid, Overdue..."
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm text-gray-900
                             focus:ring-2 focus:ring-slate-500 focus:border-slate-500
                             hover:border-gray-400 transition-all duration-200"
                    />
                  </FormField>

                  <FormField
                    label="Paid On"
                    icon={<CalendarIcon className="w-4 h-4" />}
                  >
                    <input
                      type="date"
                      name="paidOn"
                      value={project.paidOn}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm text-gray-900
                             focus:ring-2 focus:ring-slate-500 focus:border-slate-500
                             hover:border-gray-400 transition-all duration-200"
                    />
                  </FormField>
                </div>

                {/* Row 3: Payment Notes */}
                <FormField
                  label="Payment Notes"
                  icon={<DocumentIcon className="w-4 h-4" />}
                >
                  <textarea
                    name="paymentNotes"
                    value={project.paymentNotes}
                    onChange={handleInputChange}
                    rows={2}
                    placeholder="Add any payment-related notes or special instructions..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm text-gray-900
                           focus:ring-2 focus:ring-slate-500 focus:border-slate-500
                           hover:border-gray-400 transition-all duration-200 resize-none"
                  />
                </FormField>
              </div>
            </CollapsibleSection>

            {/* Tasks Section */}
            <CollapsibleSection
              title="Tasks"
              icon={<TasksIcon />}
              badge={taskSummary}
              defaultOpen={true}
            >
              <div className="space-y-4">
                <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:gap-4">
                    <div className="w-full sm:flex-1">
                      <FormField label="Project VAT (GBP)">
                        <FormControl fullWidth>
                          <Select
                            MenuProps={dropdownMenuProps}
                            name="vat"
                            value={project.vat}
                            onChange={handleProjectVatChange}
                            className="rounded-xl"
                            sx={{
                              "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#d1d5db",
                                borderRadius: "0.75rem",
                              },
                              "&:hover .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#93c5fd",
                              },
                              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#3b82f6",
                                borderWidth: "2px",
                              },
                              "& .MuiSelect-select": {
                                padding: "12px 16px",
                                fontSize: "0.875rem",
                              },
                            }}
                          >
                            <MenuItem value={0}>0%</MenuItem>
                            <MenuItem value={5}>5%</MenuItem>
                            <MenuItem value={20}>20%</MenuItem>
                          </Select>
                        </FormControl>
                      </FormField>
                    </div>

                    {showAedFields && (
                      <div className="w-full sm:flex-1">
                        <FormField label="Project VAT (AED)">
                          <FormControl fullWidth>
                            <Select
                              MenuProps={dropdownMenuProps}
                              name="vatAED"
                              value={project.vatAED}
                              onChange={handleProjectVatAEDChange}
                              className="rounded-xl"
                              sx={{
                                "& .MuiOutlinedInput-notchedOutline": {
                                  borderColor: "#d1d5db",
                                  borderRadius: "0.75rem",
                                },
                                "&:hover .MuiOutlinedInput-notchedOutline": {
                                  borderColor: "#93c5fd",
                                },
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                  borderColor: "#3b82f6",
                                  borderWidth: "2px",
                                },
                                "& .MuiSelect-select": {
                                  padding: "12px 16px",
                                  fontSize: "0.875rem",
                                },
                              }}
                            >
                              <MenuItem value={0}>0%</MenuItem>
                              <MenuItem value={5}>5%</MenuItem>
                              <MenuItem value={20}>20%</MenuItem>
                            </Select>
                          </FormControl>
                        </FormField>
                      </div>
                    )}

                    <div className="flex items-center h-[46px] px-3 rounded-lg border border-gray-200 bg-slate-50/80 sm:flex-1">
                      <FormControlLabel
                        sx={{ margin: 0 }}
                        control={
                          <Checkbox
                            checked={applyVatToAllTasks}
                            onChange={handleApplyVatToAllTasksChange}
                            size="small"
                            sx={{
                              color: "#94a3b8",
                              "&.Mui-checked": {
                                color: "#3b82f6",
                              },
                            }}
                          />
                        }
                        label={
                          <span className="text-sm font-medium text-gray-600">
                            Apply VAT to all tasks
                          </span>
                        }
                      />
                    </div>
                  </div>

                  <p className="mt-3 text-xs text-gray-500">
                    When enabled, VAT fields inside tasks are locked to the
                    project-level VAT rate.
                  </p>
                </div>

                {project.tasks.map((task, index) => (
                  <TaskCard
                    key={index}
                    task={task}
                    index={index}
                    onChange={handleTaskChange}
                    onRemove={handleRemoveTask}
                    taskOptions={taskOptions}
                    countryOptions={countryOptions}
                    projectManagerOptions={projectManagerOptions}
                    company={project.company}
                    vatLocked={applyVatToAllTasks}
                    showTaskStatus={true}
                    defaultExpanded={
                      index === newlyAddedTaskIndex ? true : undefined
                    }
                  />
                ))}

                <button
                  type="button"
                  onClick={handleAddTask}
                  className="w-full px-6 py-3 border-2 border-dashed border-gray-300 rounded-xl
                         text-gray-700 font-medium flex items-center justify-center gap-2
                         hover:border-slate-500 hover:text-slate-600 hover:bg-slate-50
                         transition-all duration-200"
                >
                  <PlusIcon className="w-5 h-5" />
                  Add Another Task
                </button>
              </div>
            </CollapsibleSection>

            {/* Documents Section */}
            <CollapsibleSection
              title="Documents"
              icon={<DocumentIcon />}
              defaultOpen={true}
            >
              <div className="space-y-3">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <p className="text-sm text-gray-700 font-medium">
                      Upload supporting documents
                    </p>
                    <p className="text-xs text-gray-500">
                      PDF, images, Office files, CSV, TXT. Max 15 MB each.
                    </p>
                  </div>
                  <label className="inline-flex items-center justify-center px-4 py-2 rounded-lg border border-gray-200 bg-white text-sm font-medium text-slate-700 shadow-sm hover:border-slate-400 hover:text-slate-900 cursor-pointer transition-all duration-200">
                    <input
                      type="file"
                      multiple
                      onChange={handleFileSelect}
                      accept=".pdf,image/*,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv"
                      className="hidden"
                    />
                    <PlusIcon className="w-4 h-4 mr-2" />
                    Add Documents
                  </label>
                </div>

                {fileError && (
                  <p className="text-sm text-red-600">{fileError}</p>
                )}

                {project.documents && project.documents.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs text-gray-600 font-semibold">
                      Existing documents
                    </p>
                    {project.documents.map((doc, index) => (
                      <div
                        key={doc.path || doc.url || index}
                        className="flex items-center justify-between border border-gray-200 rounded-xl bg-white px-4 py-3 shadow-sm"
                      >
                        <div className="flex items-center gap-3">
                          <DocumentIcon className="w-5 h-5 text-slate-600" />
                          <div>
                            <p className="text-sm font-medium text-slate-800 break-all">
                              {doc.name || `Document ${index + 1}`}
                            </p>
                            {doc.size ? (
                              <p className="text-xs text-gray-500">
                                {formatFileSize(doc.size)}
                              </p>
                            ) : null}
                          </div>
                        </div>
                        {doc.url ? (
                          <a
                            href={doc.url}
                            target="_blank"
                            rel="noreferrer"
                            className="text-xs text-slate-600 hover:text-slate-800 font-medium"
                          >
                            Open
                          </a>
                        ) : null}
                      </div>
                    ))}
                  </div>
                )}

                <div className="space-y-2">
                  {documentFiles.length === 0 && (
                    <div className="border border-dashed border-gray-300 rounded-xl p-4 text-sm text-gray-500 bg-gray-50">
                      No new documents added yet.
                    </div>
                  )}

                  {documentFiles.map((file, index) => (
                    <div
                      key={`${file.name}-${index}`}
                      className="flex items-center justify-between border border-gray-200 rounded-xl bg-white px-4 py-3 shadow-sm"
                    >
                      <div className="flex items-center gap-3">
                        <DocumentIcon className="w-5 h-5 text-slate-600" />
                        <div>
                          <p className="text-sm font-medium text-slate-800 break-all">
                            {file.name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {formatFileSize(file.size)}
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveFile(index)}
                        className="text-xs text-red-600 hover:text-red-700 font-medium"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>

                {submitDisabled && documentFiles.length > 0 && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm text-gray-600">
                      <span>Uploading documents...</span>
                      <span>{uploadProgress}%</span>
                    </div>
                    <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-green-600 transition-all duration-200"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                  </div>
                )}

                {uploadError && (
                  <p className="text-sm text-red-600">{uploadError}</p>
                )}
              </div>
            </CollapsibleSection>

            {/* Submit Button */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">
                    <span className="flex items-center gap-2 text-green-600">
                      <CheckCircleIcon className="w-4 h-4" />
                      Ready to submit
                    </span>
                  </p>
                </div>
                <button
                  type="submit"
                  disabled={submitDisabled || !isFormValid()}
                  className="px-8 py-3 bg-gradient-to-r from-slate-700 to-slate-800 text-white rounded-xl font-medium
                         hover:from-slate-800 hover:to-slate-900
                         disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed
                         active:scale-95 transition-all duration-200 shadow-md hover:shadow-xl
                         flex items-center gap-2"
                >
                  {submitDisabled ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <CheckCircleIcon className="w-5 h-5" />
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditProject;
